import React, { useContext } from "react";
import { ThemeContext } from "../../lib/main-context";
import { useTranslation } from "../../i18n/TranslationProvider";
import { translateDataValue, toMomentLocale } from "../../i18n/translateData";
import moment from "moment";
import "moment/locale/es";
import "moment/locale/zh-cn";
import "moment/locale/id";

import theme from "../../index.scss";

import { Bar } from "react-chartjs-2";
import TriggerChartOptions from "./TriggerChartOptions";
import TriggerNoStackedOptions from "./TriggerNoStackedOptions";


import getDaysAgo from "../../lib/getDaysAgo";
import fillInDates from "../../lib/fillInDates";

const TriggerChart = ({ data, height, stacked }) => {
  const { t, language } = useTranslation();

  // Set moment locale for day name translations in chart tooltips
  moment.locale(toMomentLocale(language));

  // Maps dataset subcategory keys to the onboarding.triggerSelector.tabs.* keys
  // Note: TRIGGER_LIST uses "Dietary" (capital D) so we need to handle that
  const subcategoryToTabKey = {
    wellness: "wellness",
    environmental: "environmental",
    "screen time": "screenTime",
    travel: "travel",
    situational: "situational",
    dietary: "dietary",
    other: "other",
  };

  // Maps lowercase dataset keys to TRIGGER_LIST keys (which may have different casing)
  const subcategoryToTriggerListKey = {
    wellness: "wellness",
    environmental: "environmental",
    "screen time": "screen time",
    travel: "travel",
    situational: "situational",
    dietary: "Dietary",
    other: "Other",
  };

  const { dark } = useContext(ThemeContext);
  const colors = {
    wellness: theme.chart1,
    environmental: theme.chart2,
    "screen time": theme.chart3,
    travel: theme.chart4,
    other: theme.chart5,
    situational: theme.chart6,
    dietary: theme.chart7,
  };

  let dataset = {
    wellness: [],
    environmental: [],
    "screen time": [],
    travel: [],
    situational: [],
    dietary: [],
    other: [],
  };
  let triggers = data?.filter(
    ({ category, symptom_date }) =>
      category === "SDOH" && new Date(symptom_date) > getDaysAgo(7)
  );

  triggers && triggers.forEach((trigger) => {
    dataset[trigger.subcategory.toLowerCase()].push(trigger);
  });

  const dataForChart = {
    datasets: [],
  };

  const setDataForSubcategory = (subcategoryData) => {
    let DataSortByDate = {};
    subcategoryData.forEach((item) => {
      if (DataSortByDate[item.symptom_date]) {
        DataSortByDate[item.symptom_date].push(item.factor);
      } else {
        DataSortByDate[item.symptom_date] = [item.factor];
      }
    });

    let structuredData = [];
    for (let date in DataSortByDate) {
      const formattedDate = date.slice(0, 10);
      structuredData.push({
        x: formattedDate,
        y: DataSortByDate[date].length,
        detail: DataSortByDate[date],
      });
    }

    return structuredData;
  };

  for (let subcategory in dataset) {
    dataForChart.datasets.push({
      label: t(`onboarding.triggerSelector.tabs.${subcategoryToTabKey[subcategory]}`),
      data: setDataForSubcategory(dataset[subcategory]),
      backgroundColor: colors[subcategory],
    });
  }

  fillInDates(dataForChart.datasets, 7);

  TriggerChartOptions.title.text = t("dashboard.charts.triggerData.title");
  TriggerChartOptions.title.fontColor = dark ? theme.darkModeText : theme.black;
  TriggerChartOptions.legend = {
    labels: { fontColor: dark ? theme.darkModeText : theme.black },
  };
  TriggerChartOptions.scales.xAxes[0].ticks = {
    fontColor: dark ? theme.darkModeText : theme.black,
  };
  TriggerChartOptions.scales.yAxes[0].ticks = {
    beginAtZero: true,
    stepSize: 1,
    fontColor: dark ? theme.darkModeText : theme.black,
  };
  TriggerChartOptions.tooltips = {
    callbacks: {
      label: (item, data) => {
        let pointData = data.datasets[item.datasetIndex].data[item.index].y;
        const subcategory = Object.keys(subcategoryToTabKey)[item.datasetIndex];
        const triggerListKey = subcategoryToTriggerListKey[subcategory];
        return pointData.detail.map(
          // Display only — chart data keeps the English factor names
          (factor) => translateDataValue(t, "onboarding.triggerList", triggerListKey, factor)
        ).join(", ");
      },
    },
  };
  TriggerNoStackedOptions.tooltips = {
    callbacks: {
      label: (item, data) => {
        let pointData = data.datasets[item.datasetIndex].data[item.index].y;
        const subcategory = Object.keys(subcategoryToTabKey)[item.datasetIndex];
        const triggerListKey = subcategoryToTriggerListKey[subcategory];
        return pointData.detail.map(
          // Display only — chart data keeps the English factor names
          (factor) => translateDataValue(t, "onboarding.triggerList", triggerListKey, factor)
        ).join(", ");
      },
    },
  };

  return (
    stacked == false ?
      <>
        <Bar
          key={language}
          data={dataForChart}
          height={height}
          options={{
            ...TriggerNoStackedOptions,
          }}
        />
      </>
      : <>
        <Bar
          key={language}
          data={dataForChart}
          height={height}
          options={{
            ...TriggerChartOptions,
          }}
        />
      </>
  );
};

export default TriggerChart;